import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useState } from "react";
import { Plus, Minus, Check, Camera } from "lucide-react";
import LinkedPartySelector from "./LinkedPartySelector";
import ProfitMultiPresetPicker from "./ProfitMultiPresetPicker";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (tx: { type: "Profit"|"Expense", item: string, amount: number, party?: any, details?: any }) => void;
  ventureName?: string;
  customerList: Array<{ name: string; phone?: string; email?: string }>;
  supplierList: Array<{ name: string; phone?: string; email?: string }>;
  productsList: Array<{ id: number, name: string, amount: number }>;
};

export default function AddTransactionDialog({ open, onOpenChange, onAdd, ventureName, customerList, supplierList, productsList }: Props) {
  const [step, setStep] = useState<'type'|'pick'|'custom'|'party'|'editAmt'|'discount'>("type");
  const [kind, setKind] = useState<"Profit" | "Expense" | null>(null);
  const [customName, setCustomName] = useState("");
  const [customAmt, setCustomAmt] = useState("");
  const [imgPreview, setImgPreview] = useState<string|null>(null);
  // Insert new pick values for chosen preset
  const [pendingItem, setPendingItem] = useState<{ name: string, amount: number }|null>(null);

  // Remove demo*; just use props
  // New state for linked party
  const [linkedParty, setLinkedParty] = useState<any>(null);

  // Add states for editing preset expense amount and discount
  const [editAmount, setEditAmount] = useState<string>(""); // For expense preset
  const [discount, setDiscount] = useState<string>("");     // For profit preset

  // Remove special BBA logic and all hardcoded product/service presets
  // Instead, always use productsList from props for incomeItems
  const incomeItems = productsList;
  const expenseItems = [
    { id: 1, name: "Ad Spend", amount: -80 },
    { id: 2, name: "Shipping", amount: -15 },
    { id: 3, name: "Returns/Refund", amount: -400 }
  ];

  // Add new state for multi-preset selection
  const [multiProfit, setMultiProfit] = useState<{id:number,name:string,price:number,qty:number,total:number}[]>([]);
  const [multiTotal, setMultiTotal] = useState(0);
  function start(type: "Profit" | "Expense") {
    setKind(type);
    setStep("pick");
    setCustomName(""); setCustomAmt(""); setImgPreview(null);
    setLinkedParty(null);
    setPendingItem(null);
    setEditAmount("");
    setDiscount("");
    setMultiProfit([]);
    setMultiTotal(0);
  }

  // Reset on close
  function handleOpenChange(next: boolean) {
    if (!next) {
      setStep("type");
      setKind(null);
      setCustomName("");
      setCustomAmt("");
      setImgPreview(null);
      setLinkedParty(null); // reset here
      setPendingItem(null);
      setEditAmount("");
      setDiscount("");
      setMultiProfit([]);
      setMultiTotal(0);
    }
    onOpenChange(next);
  }

  const items = kind === "Profit" ? incomeItems : expenseItems;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="[&>form]:max-h-[70vh] max-h-[75vh] sm:max-h-[520px] overflow-y-auto pb-44 md:pb-6 scroll-pb-44">
        <DialogTitle className="sr-only">Add Transaction</DialogTitle>
        {step === "type" && (
          <div className="flex flex-col gap-5 mt-3">
            <button className="bg-green-600 hover:bg-green-700 text-white font-bold py-5 px-9 rounded-xl text-lg flex items-center gap-3 justify-center transition" onClick={() => start("Profit")}> <Plus /> Money In? +</button>
            <button className="bg-red-600 hover:bg-red-700 text-white font-bold py-5 px-9 rounded-xl text-lg flex items-center gap-3 justify-center transition" onClick={() => start("Expense")}> <Minus /> Money Out? -</button>
          </div>
        )}
        {step === "pick" && kind && (
          <>
            {kind === "Profit" && (
              <form className="flex flex-col gap-3" onSubmit={e=>{e.preventDefault(); setStep('party')}}>
                <DialogDescription className="mb-2 text-base font-medium text-foreground">Select product(s) and quantities for this sale:</DialogDescription>
                <ProfitMultiPresetPicker
                  items={incomeItems}
                  defaultValue={multiProfit}
                  onChange={(items,total) => {
                    setMultiProfit(items);
                    setMultiTotal(total);
                  }}
                />
                <button type="submit" disabled={multiProfit.length===0} className="mt-2 bg-primary text-primary-foreground px-6 py-2 rounded-lg font-semibold hover:bg-primary/90 transition disabled:bg-muted disabled:text-muted-foreground">Next</button>
                <button type="button" className="text-muted-foreground underline text-sm" onClick={()=>setStep('type')}>Back</button>
              </form>
            )}
            {kind === "Expense" && (
              <div className="flex flex-col gap-3 mt-3">
                <DialogDescription className="mb-2 text-base font-medium text-foreground">Pick an expense type:</DialogDescription>
                {items.map(item => (
                  <button
                    key={item.id}
                    className="bg-muted hover:bg-background border font-medium rounded-lg px-4 py-3 flex items-center gap-5 text-base justify-between transition"
                    onClick={() => {
                      setPendingItem({ name: item.name, amount: item.amount });
                      setEditAmount(kind==='Expense' ? String(Math.abs(item.amount)) : "");
                      setDiscount("");
                      setStep(kind==='Expense' ? 'editAmt' : kind==='Profit' ? 'discount' : 'party');
                    }}
                  >
                    <span className="text-left flex-1 min-w-0 truncate">{item.name}</span>
                    <span className="text-sm font-bold text-right min-w-[64px]">{item.amount > 0 ? `+$${item.amount}` : `-$${Math.abs(item.amount)}`}</span>
                    <Check className="opacity-0 group-hover:opacity-100 transition" size={18}/>
                  </button>
                ))}
                <button className="flex items-center gap-2 text-primary font-semibold text-base bg-background hover:bg-primary/5 px-4 py-2 mt-2 border rounded-lg transition" onClick={() => setStep('custom')}><Plus />Add Entry</button>
                <button className="mt-1 text-sm text-muted-foreground underline" onClick={() => setStep("type")}>Back</button>
              </div>
            )}
          </>
        )}
        {/* Amount review for preset expense */}
        {(step==='editAmt' && kind==='Expense' && pendingItem) && (
          <form className="flex flex-col gap-4 mt-3" onSubmit={e => {
            e.preventDefault();
            setPendingItem({ name: pendingItem.name, amount: Number(editAmount) });
            setStep('party');
          }}>
            <DialogDescription className="mb-1 text-base font-medium text-foreground">Edit Amount for <span className="font-semibold">{pendingItem.name}</span></DialogDescription>
            <input
              type="number" min="0" step="0.01" required
              value={editAmount}
              className="border rounded px-3 py-2 text-base w-full"
              onChange={e=>setEditAmount(e.target.value)}
            />
            <div className="flex gap-3 pt-2">
              <button type="submit" className="bg-primary text-primary-foreground px-7 py-2 rounded-lg font-semibold hover:bg-primary/90 transition">Next</button>
              <button type="button" className="text-muted-foreground underline" onClick={()=>setStep('pick')}>Back</button>
            </div>
          </form>
        )}
        {/* Discount input for profit */}
        {(step==='discount' && kind==='Profit' && pendingItem) && (
          <form className="flex flex-col gap-4 mt-3" onSubmit={e => {
            e.preventDefault();
            // adjust amount for discount if present
            const amt = Math.max(0, Number(pendingItem.amount) - Number(discount||0));
            setPendingItem({ name: pendingItem.name, amount: amt });
            setStep('party');
          }}>
            <DialogDescription className="mb-1 text-base font-medium text-foreground">
              {(Number(discount||0)>0)
                ? (<><span className="font-semibold">{pendingItem.name}</span> (${pendingItem.amount})<br/>Discount: <span className="text-green-700">-${discount}</span><br/><span className="font-semibold">Final Receivable: ${Math.max(0, Number(pendingItem.amount)-Number(discount||0))}</span></>)
                : <>Apply Discount to <span className="font-semibold">{pendingItem.name}</span>?</>}
            </DialogDescription>
            <input
              type="number" min="0" step="0.01"
              placeholder="(optional) Discount"
              value={discount}
              className="border rounded px-3 py-2 text-base w-full"
              onChange={e=>setDiscount(e.target.value)}
            />
            <div className="flex gap-3 pt-2">
              <button type="submit" className="bg-primary text-primary-foreground px-7 py-2 rounded-lg font-semibold hover:bg-primary/90 transition">Next</button>
              <button type="button" className="text-muted-foreground underline" onClick={()=>setStep('pick')}>Back</button>
            </div>
          </form>
        )}
        {/* NEW step: party link, always after pick or custom or editAmt or discount */}
        {(step === 'party' && kind && ((kind === 'Profit' && multiProfit.length > 0) || (kind === 'Expense' && pendingItem))) && (
          <form className="flex flex-col gap-4 mt-3" onSubmit={e => {
            e.preventDefault();
            if (kind === "Profit") {
              const label = multiProfit.map(x=>`${x.name} x${x.qty}`).join(", ");
              onAdd({ type: kind, item: label, amount: multiTotal, party: linkedParty, details: multiProfit });
            } else {
              onAdd({ type: kind, item: pendingItem!.name, amount: kind === "Profit" ? Math.abs(pendingItem!.amount) : -Math.abs(pendingItem!.amount), party: linkedParty });
            }
            handleOpenChange(false);
          }}>
            <DialogDescription className="mb-1 text-base font-medium text-foreground">
              Link this {kind === 'Profit' ? 'sale or income' : 'expense'} to a {kind === 'Profit' ? 'customer' : 'supplier/preset'} (optional)
            </DialogDescription>
            {kind === "Profit" && (
              <LinkedPartySelector
                role="customer"
                partyList={customerList}
                onSelect={setLinkedParty}
              />
            )}
            {kind === "Expense" && (
              <LinkedPartySelector
                role="supplier"
                partyList={supplierList}
                onSelect={setLinkedParty}
              />
            )}
            {linkedParty && (
              <div className="text-xs text-muted-foreground mb-2 mt-1">Linked to: <span className="font-semibold text-primary">{linkedParty.name}</span></div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="submit" className="bg-primary text-primary-foreground px-7 py-2 rounded-lg font-semibold hover:bg-primary/90 transition">Add</button>
              <button type="button" className="text-muted-foreground underline" onClick={()=>{
                if(kind === 'Profit'){
                  setStep('pick');
                  setMultiProfit([]);
                  setMultiTotal(0);
                } else {
                  setStep('pick');
                  setLinkedParty(null);
                }
              }}>Back</button>
            </div>
          </form>
        )}
        {step === 'custom' && kind && (
          <form className="flex flex-col gap-4 mt-3" onSubmit={e => {
            e.preventDefault();
            if (!customName.trim() || !customAmt) return;
            setPendingItem({ name: customName.trim(), amount: parseFloat(customAmt) });
            setStep('party');
          }}>
            <DialogDescription className="mb-1 text-base font-medium text-foreground">
              Add a custom {kind === 'Profit' ? 'profit' : 'expense'} entry
            </DialogDescription>
            <input
              className="border rounded px-3 py-2 text-base w-full"
              placeholder={kind === 'Profit' ? 'Name of income item' : 'Name of expense' }
              value={customName}
              onChange={e => setCustomName(e.target.value)}
              required
              autoFocus
            />
            <div className="flex flex-col gap-2 w-full">
              <input
                className="border rounded px-3 py-2 text-base w-full"
                type="number"
                min="0"
                step="0.01"
                placeholder="Amount"
                value={customAmt}
                onChange={e => setCustomAmt(e.target.value)}
                required
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="ml-3 px-3 py-2 bg-muted hover:bg-primary/10 rounded-lg flex items-center gap-2 cursor-pointer">
                <Camera size={20}/>
                <span className="text-xs font-semibold">Scan Receipt</span>
                <input 
                  type="file" 
                  accept="image/*"
                  className="hidden"
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) setImgPreview(URL.createObjectURL(f));
                  }}
                />
              </label>
            </div>
            {imgPreview && (
              <div className="flex flex-col gap-1 items-start">
                <img src={imgPreview} alt="Preview" className="h-28 rounded shadow mt-1" />
                <span className="text-xs text-muted-foreground">(AI extraction coming soon)</span>
                <button className="text-xs text-primary underline" type="button" onClick={()=>setImgPreview(null)}>Remove</button>
              </div>
            )}
            {/* Leave LinkedPartySelector and submit buttons out here - in party step */}
            <div className="flex gap-3 pt-2">
              <button type="submit" className="bg-primary text-primary-foreground px-7 py-2 rounded-lg font-semibold hover:bg-primary/90 transition">Next</button>
              <button type="button" className="text-muted-foreground underline" onClick={()=>setStep('pick')}>Cancel</button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}